import { formatPhone } from '@chiroup/core/functions/format';
import { HealthComLocation } from '@chiroup/core/types/HealthComLocation.type';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

// The UTC plugin is idempotent to register, so doing it here removes
// any dependency on the caller also having extended it.
dayjs.extend(utc);

type IcsInput = {
  appointmentId: string;
  startTimestamp: number | string;
  durationMinutes?: number;
  providerName?: string;
  treatmentName?: string;
  patientName?: string;
  location: HealthComLocation;
};

// ICS requires TEXT-type properties to escape backslashes, commas,
// semicolons, and newlines. Line breaks must become the literal `\n`
// sequence. Spec: RFC 5545 § 3.3.11.
const escapeIcsText = (value: string): string =>
  value
    .replace(/\\/g, '\\\\')
    .replace(/\r\n|\n|\r/g, '\\n')
    .replace(/,/g, '\\,')
    .replace(/;/g, '\\;');

// UTC datetime stamp in the form RFC 5545 wants: YYYYMMDDTHHMMSSZ.
const toIcsUtc = (d: dayjs.Dayjs): string => d.utc().format('YYYYMMDDTHHmmss') + 'Z';

const buildAddress = (location: HealthComLocation): string => {
  return [
    location.address1,
    location.address2,
    [location.city, location.state].filter(Boolean).join(', '),
    location.zip,
    location.country && location.country !== 'USA' ? location.country : null,
  ]
    .filter(Boolean)
    .join(', ');
};

const buildDescription = (input: IcsInput): string => {
  const lines: string[] = [];

  if (input.treatmentName) {
    lines.push(`Treatment: ${input.treatmentName}`);
  }

  if (input.providerName) {
    lines.push(`Provider: ${input.providerName}`);
  }

  if (input.patientName) {
    lines.push(`For: ${input.patientName}`);
  }

  if (input.location.phone) {
    lines.push(`Phone: ${formatPhone(input.location.phone) || input.location.phone}`);
  }

  if (input.location.site) {
    lines.push(`Website: ${input.location.site}`);
  }

  return lines.join('\n');
};

/**
 * Builds an RFC 5545 VCALENDAR/VEVENT string for a single appointment.
 *
 * Fields chosen so popular clients (Apple Calendar, Google Calendar,
 * Outlook) light up their "get directions" / "call" affordances —
 * specifically LOCATION is the full mailing address, and DESCRIPTION
 * includes phone + website so tapping the event in Apple/Google opens
 * the phone dialer.
 */
export const buildAppointmentIcs = (input: IcsInput): string => {
  const start = dayjs(
    typeof input.startTimestamp === 'number' ? input.startTimestamp : input.startTimestamp
  );
  const end = start.add(input.durationMinutes ?? 30, 'minute');
  const summary = [input.providerName, input.treatmentName ? `(${input.treatmentName})` : null]
    .filter(Boolean)
    .join(' ');
  const address = buildAddress(input.location);
  const description = buildDescription(input);

  // Per RFC 5545: CRLF line terminators, `PRODID` identifies the
  // generator, `UID` must be unique per-event.
  const lines: string[] = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//HealthCom//Appointment//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${input.appointmentId}@healthcom`,
    `DTSTAMP:${toIcsUtc(dayjs())}`,
    `DTSTART:${toIcsUtc(start)}`,
    `DTEND:${toIcsUtc(end)}`,
    `SUMMARY:${escapeIcsText(summary || 'Appointment')}`,
  ];

  if (address) {
    lines.push(`LOCATION:${escapeIcsText(address)}`);
  }

  if (description) {
    lines.push(`DESCRIPTION:${escapeIcsText(description)}`);
  }

  lines.push('STATUS:CONFIRMED');
  lines.push('END:VEVENT');
  lines.push('END:VCALENDAR');

  return lines.join('\r\n');
};

const isIos = (): boolean => {
  const ua = navigator.userAgent;
  return (
    /iPad|iPhone|iPod/.test(ua) ||
    // iPadOS 13+ in desktop mode reports as Macintosh.
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
};

/**
 * Triggers a browser download of an ICS file for the given appointment.
 *
 * iOS Safari does not support the `download` attribute on anchors, so the
 * legacy anchor-click approach silently does nothing. On iOS/iPadOS we use
 * the Web Share API to surface the system share sheet — which includes the
 * native "Add to Calendar" action. If the Web Share API isn't available
 * we return `false` so the caller can show a hint.
 *
 * Returns `true` when we're reasonably confident the action fired, or
 * `false` when the attempt may have been silently swallowed (Web Share
 * API unavailable, etc.). Callers can use the return value
 * to show a fallback message.
 */
export const downloadAppointmentIcs = async (input: IcsInput): Promise<boolean> => {
  const ics = buildAppointmentIcs(input);
  const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
  const fileName = `appointment-${input.appointmentId}.ics`;

  // On iOS/iPadOS, the anchor+download approach silently fails. Use the
  // Web Share API which hands the .ics to the system share sheet —
  // Calendar.app picks it up natively.
  if (isIos()) {
    // Web Share API is the only reliable path on iOS/iPadOS. If it's
    // unavailable, return false so the caller can surface feedback
    // rather than falling through to the anchor path which silently
    // fails (iOS doesn't support the `download` attribute).
    if (!navigator.canShare) return false;
    const file = new File([blob], fileName, { type: 'text/calendar' });
    if (!navigator.canShare({ files: [file] })) return false;
    try {
      await navigator.share({ files: [file] });
      return true;
    } catch (err: unknown) {
      // AbortError = user tapped "Cancel" on the share sheet. That's
      // intentional, not a failure — don't show the error toast.
      if (err instanceof DOMException && err.name === 'AbortError') return true;
      return false;
    }
  }

  // Desktop / Android: the anchor+download pattern works reliably.
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);

  setTimeout(() => URL.revokeObjectURL(url), 250);

  return true;
};
